import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { getLoadIdentifier } from "../domain/load";

const STORAGE_KEY = "searchTabsState.v1";
const HIGHLIGHT_DURATION_MS = 15000;

const SearchTabsContext = createContext(undefined);

const generateId = () =>
  `${Date.now()}-${Math.random().toString(16).slice(2, 10)}`;

const createDefaultFormData = () => ({
  origin_city: "",
  origin_state: "CA",
  destination_city: "",
  destination_state: "",
  max_weight: 45000,
  max_length: 60,
  truck_types: ["V"],
  ship_date: new Date().toISOString().split("T")[0],
  origin_range: 100,
  destination_range: 100,
  min_distance: 0,
  max_distance: "",
  enableBackhaul: false,
  enableTruckstop: true,
});

const applySearchTypeFlags = (tab, searchType) => {
  if (searchType === "backhaul") {
    tab.enableBackhaul = true;
    tab.enableTruckstop = true;
    return;
  }

  // default behaviour matches legacy LoadSearch.js
  tab.enableBackhaul = false;
  tab.enableTruckstop = true;
};

const createTab = ({
  id,
  label = "Search 1",
  searchType = "standard",
  formData,
  results = null,
  loading = false,
  enableBackhaul,
  enableTruckstop,
  lastSearch = null,
  sort = "score",
  searchId = null, // SSE search session ID for this tab
} = {}) => {
  const tab = {
    id: id ?? generateId(),
    label,
    searchType,
    formData: { ...createDefaultFormData(), ...(formData || {}) },
    results,
    loading,
    enableBackhaul: enableBackhaul ?? false,
    enableTruckstop: enableTruckstop ?? true,
    lastSearch,
    createdAt: Date.now(),
    recentHighlights: {},
    sort: sort || "score",
    searchId: searchId ?? null, // Store SSE searchId per tab
  };

  if (
    enableBackhaul === undefined &&
    enableTruckstop === undefined
  ) {
    applySearchTypeFlags(tab, searchType);
  }

  return tab;
};

const hydrateState = (stored) => {
  if (!stored || !Array.isArray(stored.tabs) || stored.tabs.length === 0) {
    return null;
  }

  const hydratedTabs = stored.tabs.map((tab) => createTab(tab));
  const validActiveTabId = hydratedTabs.some(
    (tab) => tab.id === stored.activeTabId,
  )
    ? stored.activeTabId
    : hydratedTabs[0].id;

  return {
    tabs: hydratedTabs,
    activeTabId: validActiveTabId,
    counter: stored.counter ?? hydratedTabs.length + 1,
  };
};

const getInitialState = () => {
  if (typeof window !== "undefined") {
    try {
      const stored = window.localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        const hydrated = hydrateState(parsed);
        if (hydrated) {
          return hydrated;
        }
      }
    } catch (error) {
      console.warn("Failed to hydrate search tabs from storage", error);
    }
  }

  const firstTab = createTab({ label: "Search 1" });
  return {
    tabs: [firstTab],
    activeTabId: firstTab.id,
    counter: 2,
  };
};

export const SearchTabsProvider = ({ children }) => {
  const [state, setState] = useState(getInitialState);

  useEffect(() => {
    if (typeof window !== "undefined") {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    }
  }, [state]);

  const tabs = state.tabs;
  const activeTabId = state.activeTabId;

  const activeTab = useMemo(() => {
    return (
      tabs.find((tab) => tab.id === activeTabId) ||
      tabs[0] ||
      null
    );
  }, [tabs, activeTabId]);

  const setActiveTabId = useCallback((id) => {
    setState((prev) => {
      if (!prev.tabs.some((tab) => tab.id === id)) {
        return prev;
      }
      return { ...prev, activeTabId: id };
    });
  }, []);

  const addTab = useCallback((searchType = "standard", overrides = {}) => {
    setState((prev) => {
      const label =
        overrides.label || `Search ${prev.counter ?? prev.tabs.length + 1}`;
      const newTab = createTab({
        label,
        searchType,
        ...overrides,
      });
      return {
        tabs: [...prev.tabs, newTab],
        activeTabId: newTab.id,
        counter: (prev.counter ?? prev.tabs.length + 1) + 1,
      };
    });
  }, []);

  const removeTab = useCallback((tabId) => {
    setState((prev) => {
      if (prev.tabs.length <= 1) {
        return prev;
      }
      
      // Find the tab being removed to check if it has an active searchId
      const tabToRemove = prev.tabs.find((tab) => tab.id === tabId);
      if (tabToRemove?.searchId) {
        console.log(`[TAB] Removing tab ${tabId} with active searchId ${tabToRemove.searchId}`);
        // Note: The searchId will be automatically cleaned up when the tab is removed
        // because useMultiTabSSE watches the tabs array and cleans up connections
        // for tabs that no longer exist
      }
      
      const filtered = prev.tabs.filter((tab) => tab.id !== tabId);
      const nextActiveId =
        prev.activeTabId === tabId
          ? filtered[0]?.id ?? null
          : prev.activeTabId;
      return {
        ...prev,
        tabs: filtered,
        activeTabId: nextActiveId,
      };
    });
  }, []);

  const updateActiveTabFormData = useCallback((updates) => {
    setState((prev) => {
      const id = prev.activeTabId;
      if (!id) return prev;
      return {
        ...prev,
        tabs: prev.tabs.map((tab) =>
          tab.id === id
            ? {
                ...tab,
                formData: { ...tab.formData, ...updates },
                enableBackhaul:
                  updates.enableBackhaul !== undefined
                    ? Boolean(updates.enableBackhaul)
                    : tab.enableBackhaul,
              }
            : tab,
        ),
      };
    });
  }, []);

  const setTabLoading = useCallback((tabId, loading) => {
    if (!tabId) {
      return;
    }
    setState((prev) => ({
      ...prev,
      tabs: prev.tabs.map((tab) =>
        tab.id === tabId ? { ...tab, loading } : tab,
      ),
    }));
  }, []);

  const setActiveTabLoading = useCallback(
    (loading) => {
      setState((prev) => {
        const id = prev.activeTabId;
        if (!id) return prev;
        return {
          ...prev,
          tabs: prev.tabs.map((tab) =>
            tab.id === id ? { ...tab, loading } : tab,
          ),
        };
      });
    },
    [setState],
  );

  const setActiveTabResults = useCallback((results, meta = {}) => {
    setState((prev) => {
      const id = prev.activeTabId;
      if (!id) return prev;
      const timestamp = Date.now();
      const annotatedResults = Array.isArray(results)
        ? results.map((load, idx) => ({
            ...load,
            __insertedAt: timestamp + (Array.isArray(results) ? results.length - idx : 0),
          }))
        : results;
      return {
        ...prev,
        tabs: prev.tabs.map((tab) =>
          tab.id === id
            ? {
                ...tab,
                results: annotatedResults,
                loading: false,
                lastSearch: {
                  ...(tab.lastSearch || {}),
                  ...meta,
                  timestamp,
                },
                recentHighlights: {},
              }
            : tab,
        ),
      };
    });
  }, []);

  const mergeTabResults = useCallback(
    (tabId, incomingResults = [], meta = {}) => {
      if (!tabId || !Array.isArray(incomingResults)) {
        return;
      }
      const now = Date.now();
      let newHighlightIds = [];

      setState((prev) => {
        const index = prev.tabs.findIndex((tab) => tab.id === tabId);
        if (index === -1) {
          return prev;
        }
        const tab = prev.tabs[index];
        const existingResults = Array.isArray(tab.results) ? tab.results : [];
        const incomingMap = new Map();

        incomingResults.forEach((load) => {
          const id = getLoadIdentifier(load);
          if (!id) {
            return;
          }
          incomingMap.set(id, { ...load });
        });

        const updatedExisting = existingResults.map((load) => {
          const id = getLoadIdentifier(load);
          if (id && incomingMap.has(id)) {
            const merged = { ...load, ...incomingMap.get(id) };
            merged.__insertedAt = load.__insertedAt || now;
            incomingMap.delete(id);
            return merged;
          }
          return load;
        });

        const newLoads = [];
        incomingMap.forEach((load, id) => {
          newLoads.push({
            ...load,
            __insertedAt: now + newLoads.length + 1,
          });
          if (id) {
            newHighlightIds.push(id);
          }
        });

        if (newLoads.length === 0 && incomingResults.length === 0) {
          const updatedTabs = [...prev.tabs];
          updatedTabs[index] = {
            ...tab,
            loading: false,
            lastSearch: {
              ...(tab.lastSearch || {}),
              ...meta,
              timestamp: now,
            },
          };
          return { ...prev, tabs: updatedTabs };
        }

        const mergedResults = [...newLoads, ...updatedExisting];

        const prunedHighlights = {};
        const currentHighlights = tab.recentHighlights || {};
        Object.keys(currentHighlights).forEach((id) => {
          if (now - currentHighlights[id] < HIGHLIGHT_DURATION_MS) {
            prunedHighlights[id] = currentHighlights[id];
          }
        });
        newHighlightIds.forEach((id) => {
          if (id) {
            prunedHighlights[id] = now;
          }
        });

        const updatedTabs = [...prev.tabs];
        updatedTabs[index] = {
          ...tab,
          results: mergedResults,
          loading: false,
          lastSearch: {
            ...(tab.lastSearch || {}),
            ...meta,
            timestamp: now,
          },
          recentHighlights: prunedHighlights,
        };

        return { ...prev, tabs: updatedTabs };
      });

      if (newHighlightIds.length) {
        setTimeout(() => {
          setState((prev) => {
            const index = prev.tabs.findIndex((tab) => tab.id === tabId);
            if (index === -1) {
              return prev;
            }
            const tab = prev.tabs[index];
            const currentHighlights = tab.recentHighlights || {};
            const timestamp = Date.now();
            const remainingHighlights = {};
            let changed = false;
            Object.keys(currentHighlights).forEach((id) => {
              if (timestamp - currentHighlights[id] < HIGHLIGHT_DURATION_MS) {
                remainingHighlights[id] = currentHighlights[id];
              } else {
                changed = true;
              }
            });
            if (!changed) {
              return prev;
            }
            const updatedTabs = [...prev.tabs];
            updatedTabs[index] = {
              ...tab,
              recentHighlights: remainingHighlights,
            };
            return { ...prev, tabs: updatedTabs };
          });
        }, HIGHLIGHT_DURATION_MS);
      }
    },
    [setState],
  );

  const clearActiveTabResults = useCallback(() => {
    setState((prev) => {
      const id = prev.activeTabId;
      if (!id) return prev;
      return {
        ...prev,
        tabs: prev.tabs.map((tab) =>
          tab.id === id ? { ...tab, results: null, recentHighlights: {} } : tab,
        ),
      };
    });
  }, []);

  const setActiveTabSort = useCallback((sort) => {
    setState((prev) => {
      const id = prev.activeTabId;
      if (!id) return prev;
      return {
        ...prev,
        tabs: prev.tabs.map((tab) =>
          tab.id === id ? { ...tab, sort: sort || "score" } : tab,
        ),
      };
    });
  }, []);

  const setTabSort = useCallback((tabId, sort) => {
    if (!tabId) {
      return;
    }
    setState((prev) => ({
      ...prev,
      tabs: prev.tabs.map((tab) =>
        tab.id === tabId ? { ...tab, sort: sort || "score" } : tab,
      ),
    }));
  }, []);

  const setTabSearchId = useCallback((tabId, searchId) => {
    if (!tabId) {
      return;
    }
    setState((prev) => ({
      ...prev,
      tabs: prev.tabs.map((tab) =>
        tab.id === tabId ? { ...tab, searchId } : tab,
      ),
    }));
  }, []);

  const setActiveTabSearchId = useCallback((searchId) => {
    setState((prev) => {
      const id = prev.activeTabId;
      if (!id) return prev;
      return {
        ...prev,
        tabs: prev.tabs.map((tab) =>
          tab.id === id ? { ...tab, searchId } : tab,
        ),
      };
    });
  }, []);

  const value = useMemo(
    () => ({
      tabs,
      activeTabId,
      activeTab,
      setActiveTabId,
      addTab,
      removeTab,
      updateActiveTabFormData,
      setActiveTabResults,
      mergeTabResults,
      setActiveTabLoading,
      setTabLoading,
      clearActiveTabResults,
      setActiveTabSort,
      setTabSort,
      setTabSearchId,
      setActiveTabSearchId,
    }),
    [
      tabs,
      activeTabId,
      activeTab,
      setActiveTabId,
      addTab,
      removeTab,
      updateActiveTabFormData,
      setActiveTabResults,
      mergeTabResults,
      setActiveTabLoading,
      setTabLoading,
      clearActiveTabResults,
      setActiveTabSort,
      setTabSort,
      setTabSearchId,
      setActiveTabSearchId,
    ],
  );

  return (
    <SearchTabsContext.Provider value={value}>
      {children}
    </SearchTabsContext.Provider>
  );
};

export const useSearchTabs = () => {
  const context = useContext(SearchTabsContext);
  if (!context) {
    throw new Error("useSearchTabs must be used within a SearchTabsProvider");
  }
  return context;
};


